// AI 的头像（设置 → AI 头像里自己选，只记在本机）：点阵小球（thinking-orbs）或开源的机器人头像（DiceBear：
// Bottts / Bottts Neutral 是 Pablo Stanley 的设计，个人和商用都免费；Glass 是 MIT）。头像在浏览器里画，不连网。
// 回答前「在想」的时候，过程那一行另有按在做的事换动作的小球（Thinking.tsx），和头像无关。

import { useMemo, useSyncExternalStore } from 'react'
import { createAvatar } from '@dicebear/core'
import * as bottts from '@dicebear/bottts'
import * as botttsNeutral from '@dicebear/bottts-neutral'
import * as glass from '@dicebear/glass'
import { Orb, type OrbState } from './Thinking'

export type AvatarChoice = { kind: 'orb'; state: OrbState } | { kind: 'bottts' | 'bottts-neutral' | 'glass'; seed: string }

const STYLES = { bottts, 'bottts-neutral': botttsNeutral, glass } as const
const KEY = 'lingzhi-web.ai-avatar'
export const DEFAULT_AVATAR: AvatarChoice = { kind: 'orb', state: 'breathing' }

/** 设置里给的几组：每组几个，点了就用。 */
export const AVATAR_GROUPS: { label: string; note: string; items: AvatarChoice[] }[] = [
  { label: '点阵', note: '不动时是一帧，打招呼时慢慢动', items: (['breathing', 'searching', 'weaving', 'working', 'solving'] as OrbState[]).map((state) => ({ kind: 'orb', state })) },
  { label: '机器人', note: 'Bottts · Pablo Stanley', items: ['Lingzhi', 'Felix', 'Nova', 'Milo', 'Aria', 'Kiwi'].map((seed) => ({ kind: 'bottts', seed })) },
  { label: '简约机器人', note: 'Bottts Neutral', items: ['Lingzhi', 'Echo', 'Luna', 'Pixel', 'Atlas', 'Mochi'].map((seed) => ({ kind: 'bottts-neutral', seed })) },
  { label: '玻璃', note: 'Glass', items: ['Lingzhi', 'Dawn', 'Sea', 'Moss'].map((seed) => ({ kind: 'glass', seed })) },
]

export const sameAvatar = (a: AvatarChoice, b: AvatarChoice) => a.kind === b.kind && (a.kind === 'orb' ? a.state === (b as { state: string }).state : a.seed === (b as { seed: string }).seed)

const listeners = new Set<() => void>()
let cached: { raw: string | null; value: AvatarChoice } | null = null
function read(): AvatarChoice {
  let raw: string | null = null
  try {
    raw = localStorage.getItem(KEY)
  } catch {
    // 读不到就用默认的
  }
  if (cached && cached.raw === raw) return cached.value
  let value = DEFAULT_AVATAR
  try {
    const v = JSON.parse(raw ?? 'null') as AvatarChoice | null
    if (v && AVATAR_GROUPS.some((g) => g.items.some((x) => sameAvatar(x, v)))) value = v
  } catch {
    // 坏了就用默认的
  }
  cached = { raw, value }
  return value
}
export function setAvatar(v: AvatarChoice) {
  try {
    localStorage.setItem(KEY, JSON.stringify(v))
  } catch {
    // 记不住也没关系
  }
  listeners.forEach((l) => l())
}
const subscribe = (l: () => void) => {
  listeners.add(l)
  return () => listeners.delete(l)
}
export const useAvatar = () => useSyncExternalStore(subscribe, read, () => DEFAULT_AVATAR)

/** 画出来的 SVG（data: 地址），同一个只画一次。 */
const drawn = new Map<string, string>()
function svgUri(kind: 'bottts' | 'bottts-neutral' | 'glass', seed: string) {
  const key = `${kind}:${seed}`
  let uri = drawn.get(key)
  if (!uri) {
    uri = createAvatar(STYLES[kind], { seed, radius: kind === 'bottts' ? 0 : 50 }).toDataUri()
    drawn.set(key, uri)
  }
  return uri
}

export function AvatarMark({ choice, size = 20, live = false, className }: { choice: AvatarChoice; size?: 20 | 32 | 64; live?: boolean; className?: string }) {
  const uri = useMemo(() => (choice.kind === 'orb' ? null : svgUri(choice.kind, choice.seed)), [choice])
  if (choice.kind === 'orb') return <Orb state={choice.state} size={size} paused={!live} className={className} />
  return <img src={uri!} width={size} height={size} alt="" aria-hidden="true" className={['sk-avatar', className].filter(Boolean).join(' ')} draggable={false} />
}

/** 用户选的那个。live：打招呼的大头像让小球慢慢动。 */
export function AiAvatar(props: { size?: 20 | 32 | 64; live?: boolean; className?: string }) {
  return <AvatarMark choice={useAvatar()} {...props} />
}
